import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';

import isNil from 'lodash/isNil';

import { legacyTheme, styled } from '@prose-ui/legacy';

import Card from 'Components/Card';
import { breakpoints } from 'Components/ThemeProvider';

import consultationCategoryProp from 'PropTypes/consultationProps';
import { srcOrNextImageProp } from 'PropTypes/imageProps';

import useMedia from 'utils/useMedia';

import * as consultationSelectors from 'dux/consultation/selectors';

// We explicitly want the unconnected version, Question will supply the needed props
import { QuestionLayout } from './QuestionLayout';

const CardContainer = styled.div`
  flex: 1 0 auto;
  margin: 15px 0;
  max-width: 100%;

  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-content: flex-start;

  ${legacyTheme.breakpoints.up('sm')} {
    max-width: revert;
  }
`;

const CardContainerCompact = styled(CardContainer)`
  display: grid;
  grid-template-columns: repeat(2, 1fr);

  ${legacyTheme.breakpoints.up('sm')} {
    grid-template-columns: repeat(3, 1fr);
  }
  ${legacyTheme.breakpoints.up('md')} {
    grid-template-columns: repeat(5, 1fr);
  }
`;

const CardContainerOneColumn = styled(CardContainer)`
  ${legacyTheme.breakpoints.up('md')} {
    display: grid;
    grid-template-columns: 1fr;
  }
`;

const CardContainerTwoColumns = styled(CardContainer)`
  ${legacyTheme.breakpoints.up('sm')} {
    max-width: 750px;
  }
`;

const CardArea = ({ compactMode, twoColumns, hasImg, optLength, children }) => {
  const hasOneColumnLayout = !twoColumns && optLength <= 6 && !hasImg;
  const hasTwoColumnsLayout = twoColumns || optLength > 6;

  if (compactMode) {
    return <CardContainerCompact>{children}</CardContainerCompact>;
  }

  if (hasOneColumnLayout) {
    return <CardContainerOneColumn>{children}</CardContainerOneColumn>;
  }

  if (hasTwoColumnsLayout) {
    return <CardContainerTwoColumns>{children}</CardContainerTwoColumns>;
  }

  return <CardContainer>{children}</CardContainer>;
};

CardArea.propTypes = {
  compactMode: PropTypes.bool,
  twoColumns: PropTypes.bool,
  hasImg: PropTypes.bool,
  optLength: PropTypes.number.isRequired,
  children: PropTypes.node.isRequired,
};

CardArea.defaultProps = {
  compactMode: false,
  hasImg: false,
  twoColumns: false,
};

// ensure that array is present before cheching
const safeIncludes = (arr, value) => Array.isArray(arr) && arr.includes(value);
const computeValues = (initialValue, options, multi) => {
  const initialOption = option =>
    multi ? safeIncludes(initialValue, option.value) : initialValue === option.value;

  return options.reduce((acc, option) => ({ ...acc, [option.value]: initialOption(option) }), {});
};

export const Question = ({
  CardProps,
  consultationCategory,
  cta,
  initialValue,
  multi,
  onNext,
  options,
  standalone,
  name,
  questionSet,
  ...props
}) => {
  const [values, setValues] = useState(() => computeValues(initialValue, options, multi));

  useEffect(() => {
    setValues(computeValues(initialValue, options, multi));
  }, [initialValue, options, multi]);

  const questionsWithStylingGelLayout = ['styles'];

  const selectedOptions = options.filter(option => values[option.value]);
  const selectedList = selectedOptions.map(option => option.value);

  const onOptionClick = option => () => {
    const { value } = option;

    // We don't want to unselect the answer when the customer click on an already selected answer because it goes directly to the next question
    if (values[value] && !multi) {
      onNext(value);
      return;
    }

    const unique = multi ? option.unique : true;
    const selectedUnique = !multi || selectedOptions.some(opt => opt.unique);

    const o = {};
    if (unique || selectedUnique) {
      selectedList.forEach(value => {
        o[value] = false;
      });
    }
    o[value] = !values[value];

    setValues({ ...values, ...o });
    if (!multi) {
      onNext(value);
    }
  };

  const disabled = !selectedList?.length;
  const submit = () => {
    const selected = selectedList?.length ? selectedList[0] : null;
    onNext(multi ? selectedList : selected);
  };

  const isMobile = useMedia(`(max-width: ${breakpoints.sm}px)`);
  const showStylingGelLayout = isMobile && questionsWithStylingGelLayout.includes(name);
  return (
    <QuestionLayout
      buttonLabel={cta}
      consultationCategory={consultationCategory}
      disabled={disabled}
      hideFooter={!multi && isNil(initialValue)}
      multi={multi}
      questionSet={questionSet}
      showProgressTracker={!questionSet?.options?.showSteps ?? true}
      showSteps={questionSet?.options?.showSteps ?? false}
      submit={submit}
      {...props}
    >
      <CardArea
        compactMode={CardProps?.compactMode}
        data-testid="consultation-options-area"
        hasImg={options.some(opt => opt.imageUrl)}
        optLength={options.length}
        twoColumns={CardProps?.twoColumns}
      >
        {options.map((option, index) => (
          <Card
            {...CardProps}
            key={index}
            dataClick={option.dataClick}
            dataFrom={option.dataFrom}
            dataTestId={option.dataTestId}
            description={option.description}
            icon={option.icon}
            imageUrl={option.imageUrl}
            label={option.label}
            onClick={onOptionClick(option)}
            selectable={
              !selectedList?.length > 0 || (multi && !selectedOptions.find(opt => opt.unique))
            }
            selected={values[option.value] ?? false}
            selectedIcon={option.selectedIcon}
            showStylingGelLayout={showStylingGelLayout}
            theme="consultation"
          />
        ))}
      </CardArea>
    </QuestionLayout>
  );
};

Question.defaultProps = {
  classes: null,
  multi: false,
};

// multi defines if it is possible to select multiple answers
//
// initial value should correspond to the value of an option
// if multi, initial value should be an array
//
// onNext will be called with the selection
// if multi, result will be an array

Question.propTypes = {
  CardProps: PropTypes.shape({
    compactMode: PropTypes.bool,
    twoColumns: PropTypes.bool,
  }),
  classes: PropTypes.objectOf(PropTypes.string),
  consultationCategory: consultationCategoryProp.isRequired,
  cta: PropTypes.string.isRequired,
  initialValue: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
  ]),
  multi: PropTypes.bool,
  onNext: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      icon: srcOrNextImageProp,
      selectedIcon: srcOrNextImageProp,
      imageUrl: srcOrNextImageProp,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.shape()]).isRequired,
      unique: PropTypes.bool,
      value: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.number]).isRequired,
    })
  ).isRequired,
  standalone: PropTypes.bool,
  name: PropTypes.string.isRequired,
  // questionSet is an array with a an optional property "options"
  questionSet: PropTypes.oneOfType([
    // Union
    PropTypes.arrayOf(PropTypes.shape({})), // Array Of Question
    PropTypes.shape({ options: PropTypes.shape({ showSteps: PropTypes.bool }) }), // options property
  ]).isRequired,
};

export default connect(
  (state, { consultationCategory, consultationSubsetCategory, questionIndex, routeParams }) => ({
    ...consultationSelectors.getConsultationQuestionForIndex(state, {
      consultationCategory,
      consultationSubsetCategory,
      questionIndex,
      routeParams,
    }),
    options: consultationSelectors.getOptionsForQuestion(state, {
      consultationCategory,
      consultationSubsetCategory,
      questionIndex,
      routeParams,
    }),
    tipContent: consultationSelectors.getTipForQuestion(state, {
      consultationCategory,
      consultationSubsetCategory,
      questionIndex,
      routeParams,
    }),
  })
)(Question);
